/**
 * entityResolutionParts.jsx — presentational sub-components for EntityResolutionTab.
 * Extracted verbatim from components/EntityResolutionTab.jsx (god-file decomposition).
 * 11 components: ResCard, AISuggestionBlock, CorrectionForm, MappingSummary,
 * SearchableDropdown, MultiTagDropdown, BulkBar, SelectAllRow, QueryTypeLegend,
 * QueryTester (incl. inner DimTag), MetricsView. Completeness machine-verified.
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { erFetch, getBase, STATUS_COLORS, TYPE_ICONS, QUERY_TYPE_INFO, CONFIDENCE_LEVELS, QUERY_TYPES, ALL_SPORTSBOOK_NAMES, st } from './entityResolutionConstants';

export function ResCard({ r, sel, onSel, highlighted, editing, form, setForm, dd, onApprove, onReject, onEdit, onCancel, onSave, onAcceptSuggestion }) {
  const created = r.created_at?._seconds ? new Date(r.created_at._seconds * 1000).toLocaleDateString() : '—';
  const m = r.metadata || {};
  const confidence = m.confidence || (r.status === 'confirmed' || r.status === 'corrected' ? 1.0 : r.status === 'auto' ? 0.85 : null);
  const confLevel = confidence != null ? CONFIDENCE_LEVELS.find(l => confidence >= l.min) || CONFIDENCE_LEVELS[2] : null;
  const userComment = m.user_comment;

  // Build arrays to display (support array or single)
  const displaySports = m.sports?.length > 0 ? m.sports : (r.sport ? [r.sport] : []);
  const displayLeagues = m.leagues?.length > 0 ? m.leagues : (r.league ? [r.league] : []);
  const displayTeams = m.teams?.length > 0 ? m.teams : (m.team ? [m.team] : []);
  const displayPlayers = m.players?.length > 0 ? m.players : (m.player ? [m.player] : (r.type === 'player' ? [r.resolved_name] : []));
  const displayMarkets = m.markets?.length > 0 ? m.markets : (m.market || (r.type === 'market' ? r.resolved_name : null)) ? [m.market || r.resolved_name] : [];

  // Full ordered mapping: broadest → narrowest — ALWAYS show all dimensions
  const allDimensions = [
    ...displaySports.map((s, i) => [`Sport${displaySports.length > 1 ? ` ${i+1}` : ''}`, s, '#8b5cf6', 'fitness']),
    ...(displaySports.length === 0 ? [['Sport', null, '#8b5cf6', 'fitness']] : []),
    ...displayLeagues.map((l, i) => [`League${displayLeagues.length > 1 ? ` ${i+1}` : ''}`, l?.toUpperCase(), '#3b82f6', 'globe']),
    ...(displayLeagues.length === 0 ? [['League', null, '#3b82f6', 'globe']] : []),
    ...displayTeams.map((t, i) => [`Team${displayTeams.length > 1 ? ` ${i+1}` : ''}`, t, '#60a5fa', 'people']),
    ...(displayTeams.length === 0 ? [['Team', null, '#60a5fa', 'people']] : []),
    ['Opponent', m.away_team, '#60a5fa', 'swap-horizontal'],
    ...displayPlayers.map((p, i) => [`Player${displayPlayers.length > 1 ? ` ${i+1}` : ''}`, p, '#c084fc', 'person']),
    ...(displayPlayers.length === 0 ? [['Player', null, '#c084fc', 'person']] : []),
    ...displayMarkets.map((mkt, i) => [`Market${displayMarkets.length > 1 ? ` ${i+1}` : ''}`, mkt, '#f59e0b', 'trending-up']),
    ...(displayMarkets.length === 0 ? [['Market', null, '#f59e0b', 'trending-up']] : []),
    ['Line', m.line != null ? String(m.line) : null, '#fff', 'remove'],
    ['Direction', m.direction ? m.direction.charAt(0).toUpperCase() + m.direction.slice(1) : null, m.direction === 'over' ? '#10b981' : m.direction === 'under' ? '#ef4444' : '#6b7280', 'swap-vertical'],
  ];
  const filledCount = allDimensions.filter(([, v]) => v).length;
  const totalDims = allDimensions.length;

  return (
    <View style={[st.card, sel && st.cardSel, highlighted && st.cardHighlight, r.status === 'user_reported' && { borderLeftWidth: 3, borderLeftColor: '#a78bfa' }]}>
      <View style={st.cardTop}>
        <TouchableOpacity style={st.cbWrap} onPress={onSel}>
          <View style={[st.cb, sel && st.cbOn]}>{sel && <Ionicons name="checkmark" size={12} color="#000" />}</View>
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          {/* Top row: status + type + confidence + meta */}
          <View style={st.badges}>
            <View style={[st.badge, { backgroundColor: STATUS_COLORS[r.status] || '#666' }]}>
              <Text style={st.badgeTxt}>{r.status === 'user_reported' ? 'user report' : r.status}</Text>
            </View>
            <View style={st.badgeOut}>
              <Ionicons name={TYPE_ICONS[r.type] || 'help'} size={10} color="rgba(255,255,255,0.6)" />
              <Text style={st.badgeOutTxt}>{r.type}</Text>
            </View>
            {confLevel && (
              <View style={[st.badgeOut, { borderColor: confLevel.color + '40' }]}>
                <Ionicons name={confLevel.icon} size={10} color={confLevel.color} />
                <Text style={[st.badgeOutTxt, { color: confLevel.color }]}>{confLevel.label} ({(confidence * 100).toFixed(0)}%)</Text>
              </View>
            )}
            {r.source === 'user_feedback' && (
              <View style={[st.badge, { backgroundColor: 'rgba(167,139,250,0.2)' }]}>
                <Ionicons name="person" size={9} color="#a78bfa" />
                <Text style={[st.badgeTxt, { color: '#a78bfa' }]}> user</Text>
              </View>
            )}
            <Text style={st.meta}>{r.use_count || 0}x used</Text>
            <Text style={st.meta}>{created}</Text>
          </View>

          {/* Input term */}
          <View style={st.inputBox}>
            <Text style={st.boxLabel}>INPUT</Text>
            <Text style={st.inputTxt}>"{r.input_term}"</Text>
          </View>

          <View style={{ alignItems: 'center', paddingVertical: 3 }}><Ionicons name="arrow-down" size={14} color="rgba(255,255,255,0.15)" /></View>

          {/* Full structured mapping: broadest → narrowest — ALL dimensions shown */}
          <View style={st.mapGrid}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Text style={st.boxLabel}>RESOLVES TO</Text>
              <Text style={[st.boxLabel, { color: filledCount === totalDims ? '#10b981' : '#f59e0b' }]}>
                {filledCount}/{totalDims} mapped
              </Text>
            </View>
            <View style={st.mapCols}>
              {allDimensions.map(([label, value, color, icon], i) => {
                const isFilled = !!value;
                return (
                  <View key={i} style={st.mapCell}>
                    <View style={[st.mapCellDot, { backgroundColor: isFilled ? (color || '#6b7280') + '20' : 'rgba(255,255,255,0.03)' }]}>
                      <Ionicons name={icon} size={10} color={isFilled ? (color || '#6b7280') : 'rgba(255,255,255,0.12)'} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[st.mapKey, !isFilled && { color: 'rgba(255,255,255,0.15)' }]}>{label}</Text>
                      {isFilled ? (
                        <Text style={[st.mapVal, { color: color || '#fff' }]} numberOfLines={1}>
                          {label === 'Query Type' ? (QUERY_TYPE_INFO[value]?.label || value) : value}
                        </Text>
                      ) : (
                        <Text style={st.mapMissing}>—</Text>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {userComment && (
            <View style={st.commentBox}>
              <Ionicons name="chatbubble-outline" size={11} color="rgba(167,139,250,0.6)" />
              <Text style={st.commentTxt}>{userComment}</Text>
            </View>
          )}

          {/* AI Suggestion */}
          {r.ai_suggestion && !editing && <AISuggestionBlock sug={r.ai_suggestion} currentMeta={m} currentSport={r.sport} currentLeague={r.league} onAccept={() => onAcceptSuggestion?.(r.ai_suggestion)} />}
        </View>
      </View>

      {editing ? (
        <View style={st.editPanel}>
          <Text style={st.editTitle}>Edit Resolution Mapping</Text>
          <Text style={st.editSub}>Specify exactly what "{r.input_term}" should resolve to, from broadest to most specific</Text>
          <CorrectionForm form={form} setForm={setForm} dd={dd} onSubmit={onSave} onCancel={onCancel} />
        </View>
      ) : (
        <View style={st.actions}>
          {r.status !== 'confirmed' && (
            <TouchableOpacity style={st.actBtn} onPress={onApprove}>
              <Ionicons name="checkmark-circle" size={15} color="#10b981" /><Text style={[st.actTxt, { color: '#10b981' }]}>Approve</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={st.actBtn} onPress={onEdit}>
            <Ionicons name="create-outline" size={15} color="#3b82f6" /><Text style={[st.actTxt, { color: '#3b82f6' }]}>Edit</Text>
          </TouchableOpacity>
          {r.status !== 'rejected' && (
            <TouchableOpacity style={st.actBtn} onPress={onReject}>
              <Ionicons name="close-circle" size={15} color="#ef4444" /><Text style={[st.actTxt, { color: '#ef4444' }]}>Reject</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// AI Suggestion Block — shows the AI's recommended mapping with diffs
// ═══════════════════════════════════════════════════════════════════════
export function AISuggestionBlock({ sug, currentMeta, currentSport, currentLeague, onAccept }) {
  const toArr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  const join = (arr) => arr.length > 0 ? arr.join(', ') : null;
  const joinUpper = (arr) => arr.length > 0 ? arr.map(x => x?.toUpperCase()).join(', ') : null;

  const sugSports = toArr(sug.sport);
  const sugLeagues = toArr(sug.league);
  const sugTeams = toArr(sug.team);
  const sugPlayers = toArr(sug.player);
  const sugMarkets = toArr(sug.market);

  const curSports = toArr(currentMeta.sports?.length ? currentMeta.sports : currentSport);
  const curLeagues = toArr(currentMeta.leagues?.length ? currentMeta.leagues : currentLeague);
  const curTeams = toArr(currentMeta.teams?.length ? currentMeta.teams : currentMeta.team);
  const curPlayers = toArr(currentMeta.players?.length ? currentMeta.players : currentMeta.player);
  const curMarkets = toArr(currentMeta.markets?.length ? currentMeta.markets : currentMeta.market);

  const sugQueryTypes = toArr(sug.queryTypes);
  const sugSportsbooks = toArr(sug.sportsbooks);
  const curQueryTypes = toArr(currentMeta.queryTypes);
  const curSportsbooks = toArr(currentMeta.sportsbooks);

  const sugDims = [
    ['Sport', join(sugSports), join(curSports), '#8b5cf6', 'fitness'],
    ['League', joinUpper(sugLeagues), joinUpper(curLeagues), '#3b82f6', 'globe'],
    ['Team', join(sugTeams), join(curTeams), '#60a5fa', 'people'],
    ['Opponent', sug.away_team, currentMeta.away_team, '#60a5fa', 'swap-horizontal'],
    ['Player', join(sugPlayers), join(curPlayers), '#c084fc', 'person'],
    ['Market', join(sugMarkets), join(curMarkets), '#f59e0b', 'trending-up'],
    ['Line', sug.line != null ? String(sug.line) : null, currentMeta.line != null ? String(currentMeta.line) : null, '#fff', 'remove'],
    ['Direction', sug.direction, currentMeta.direction, sug.direction === 'over' ? '#10b981' : sug.direction === 'under' ? '#ef4444' : '#6b7280', 'swap-vertical'],
    ['Query Type', join(sugQueryTypes) || null, join(curQueryTypes) || null, '#a78bfa', 'flash'],
    ['Books', join(sugSportsbooks) || null, join(curSportsbooks) || null, '#f472b6', 'book'],
  ];
  const filledSug = sugDims.filter(([, v]) => v).length;
  const hasNewInfo = sugDims.some(([, sugVal, curVal]) => sugVal && (!curVal || sugVal.toLowerCase() !== curVal?.toLowerCase()));

  if (filledSug === 0) return null;

  const confPct = sug.confidence != null ? (sug.confidence * 100).toFixed(0) : '?';

  return (
    <View style={st.aiSugBox}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
          <Ionicons name="sparkles" size={12} color="#a78bfa" />
          <Text style={st.aiSugTitle}>AI Suggestion</Text>
          <Text style={st.aiSugConf}>{confPct}% confident</Text>
        </View>
        {hasNewInfo && onAccept && (
          <TouchableOpacity style={st.aiAcceptBtn} onPress={onAccept}>
            <Ionicons name="arrow-forward-circle" size={13} color="#a78bfa" />
            <Text style={st.aiAcceptTxt}>Use this</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={st.mapCols}>
        {sugDims.map(([label, sugVal, curVal, color, icon], i) => {
          if (!sugVal) return null;
          const isNew = sugVal && (!curVal || sugVal.toLowerCase() !== curVal?.toLowerCase());
          return (
            <View key={i} style={[st.mapCell, isNew && { backgroundColor: 'rgba(167,139,250,0.06)', borderRadius: 6 }]}>
              <View style={[st.mapCellDot, { backgroundColor: (color || '#6b7280') + '20' }]}>
                <Ionicons name={icon} size={10} color={color || '#6b7280'} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={st.mapKey}>{label}{isNew ? ' *' : ''}</Text>
                <Text style={[st.mapVal, { color: isNew ? '#a78bfa' : (color || '#fff') }]} numberOfLines={1}>{sugVal}</Text>
              </View>
            </View>
          );
        })}
      </View>
      {sug.reasoning ? (
        <Text style={st.aiReasoning}>{sug.reasoning}</Text>
      ) : null}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Correction Form — shared between inline edit and "Manual Add"
// ═══════════════════════════════════════════════════════════════════════
export function CorrectionForm({ form, setForm, dd, isAdd, onSubmit, onCancel, loading }) {
  const [openField, setOpenField] = useState(null);
  const [searchQ, setSearchQ] = useState('');

  const sportsFromData = dd?.sports?.length ? dd.sports : [];
  const leagueToSport = dd?.leagueToSport || {};

  const activeSports = form.sports?.length ? form.sports : (form.sport ? [form.sport] : []);
  const activeLeagues = form.leagues?.length ? form.leagues : (form.league ? [form.league] : []);

  // STRICT cascade: Sport → Leagues (only leagues for selected sports)
  const leaguesForSport = useMemo(() => {
    if (!dd?.leagues) return [];
    if (!activeSports.length) return dd.leagues;
    const sportSet = new Set(activeSports.map(s => s.toLowerCase()));
    return dd.leagues.filter(l => {
      const lSport = leagueToSport[l] || leagueToSport[l.toLowerCase()];
      return lSport && sportSet.has(lSport.toLowerCase());
    });
  }, [dd, activeSports, leagueToSport]);

  // STRICT cascade: League → Teams (only teams for selected leagues, or sport if no league)
  const teamsForLeague = useMemo(() => {
    if (!dd?.teams) return [];
    let items = dd.teams;
    if (activeLeagues.length) {
      const ls = new Set(activeLeagues.map(l => l.toLowerCase()));
      items = items.filter(t => t.league && ls.has(t.league.toLowerCase()));
    } else if (activeSports.length) {
      const ss = new Set(activeSports.map(s => s.toLowerCase()));
      items = items.filter(t => t.sport && ss.has(t.sport.toLowerCase()));
    }
    return items;
  }, [dd, activeSports, activeLeagues]);

  // STRICT cascade: Team → Players (team-first, then league, then sport)
  const playersForTeam = useMemo(() => {
    if (!dd?.players) return [];
    let items = dd.players;
    if (activeLeagues.length) {
      const ls = new Set(activeLeagues.map(l => l.toLowerCase()));
      items = items.filter(p => p.league && ls.has(p.league.toLowerCase()));
    } else if (activeSports.length) {
      const ss = new Set(activeSports.map(s => s.toLowerCase()));
      items = items.filter(p => p.sport && ss.has(p.sport.toLowerCase()));
    }
    const teams = form.teams?.length ? form.teams : (form.team ? [form.team] : []);
    if (teams.length) {
      const teamSet = new Set(teams.map(t => t.toLowerCase()));
      const teamFiltered = items.filter(p => p.team && teamSet.has(p.team.toLowerCase()));
      if (teamFiltered.length > 0) {
        const rest = items.filter(p => !p.team || !teamSet.has(p.team.toLowerCase()));
        return [...teamFiltered, ...rest];
      }
    }
    return items;
  }, [dd, activeSports, activeLeagues, form.team, form.teams]);

  // STRICT cascade: Sport → Markets (only markets for selected sports)
  const marketsForSport = useMemo(() => {
    if (!dd?.markets) return [];
    if (!activeSports.length) return dd.markets;
    const sportSet = new Set(activeSports.map(s => s.toLowerCase()));
    return dd.markets.filter(m => {
      if (!m.sports?.length) return false;
      return m.sports.some(s => sportSet.has(s.toLowerCase()));
    });
  }, [dd, activeSports]);

  // Direction options based on selected markets
  const directionOptions = useMemo(() => {
    const markets = form.markets?.length ? form.markets : (form.market ? [form.market] : []);
    const lower = markets.map(m => m.toLowerCase()).join(' ');
    if (lower.includes('odd/even') || lower.includes('will') || lower.includes('first to') || lower.includes('draw no bet') || lower.includes('halftime')) {
      return ['yes', 'no'];
    }
    return ['over', 'under'];
  }, [form.markets, form.market]);

  const buildResults = useCallback((query, pool) => {
    if (!pool?.length) return [];
    if (!query || query.length < 1) return pool.slice(0, 75);
    const q = query.toLowerCase();
    return pool.filter(item => {
      const name = typeof item === 'string' ? item : item.name;
      return name?.toLowerCase().includes(q);
    }).sort((a, b) => {
      const an = (typeof a === 'string' ? a : a.name).toLowerCase();
      const bn = (typeof b === 'string' ? b : b.name).toLowerCase();
      if (an.startsWith(q) !== bn.startsWith(q)) return an.startsWith(q) ? -1 : 1;
      return an.localeCompare(bn);
    }).slice(0, 50);
  }, []);

  const handleToggleTeam = (item) => {
    const current = form.teams || [];
    const exists = current.includes(item.name);
    const updated = exists ? current.filter(t => t !== item.name) : [...current, item.name];
    const upd = { ...form, teams: updated, team: updated[0] || '' };
    if (!exists) {
      // Auto-set sport from league mapping if available
      const sport = item.sport || (item.league ? leagueToSport[item.league] || leagueToSport[item.league?.toLowerCase()] : null);
      if (sport) {
        const curSports = upd.sports || (upd.sport ? [upd.sport] : []);
        if (!curSports.some(s => s.toLowerCase() === sport.toLowerCase())) {
          upd.sports = [...curSports, sport]; upd.sport = upd.sports[0];
        }
      }
      if (item.league) {
        const curLeagues = upd.leagues || (upd.league ? [upd.league] : []);
        if (!curLeagues.some(l => l.toLowerCase() === item.league.toLowerCase())) {
          upd.leagues = [...curLeagues, item.league]; upd.league = upd.leagues[0];
        }
      }
    }
    setForm(upd);
  };

  const handleRemoveTeam = (name) => {
    const updated = (form.teams || []).filter(t => t !== name);
    setForm(p => ({ ...p, teams: updated, team: updated[0] || '' }));
  };

  const handleSelectAwayTeam = (item) => {
    const upd = { ...form, away_team: item.name };
    if (item.sport && !form.sport) upd.sport = item.sport;
    if (item.league && !form.league) upd.league = item.league;
    setForm(upd);
    setOpenField(null);
  };

  const handleTogglePlayer = (item) => {
    const current = form.players || [];
    const exists = current.includes(item.name);
    const updated = exists ? current.filter(p => p !== item.name) : [...current, item.name];
    const upd = { ...form, players: updated, player: updated[0] || '' };
    if (!exists) {
      if (item.team && !(form.teams?.length)) { upd.teams = [item.team]; upd.team = item.team; }
      const sport = item.sport || (item.league ? leagueToSport[item.league] || leagueToSport[item.league?.toLowerCase()] : null);
      if (sport) {
        const curSports = upd.sports || (upd.sport ? [upd.sport] : []);
        if (!curSports.some(s => s.toLowerCase() === sport.toLowerCase())) {
          upd.sports = [...curSports, sport]; upd.sport = upd.sports[0];
        }
      }
      if (item.league) {
        const curLeagues = upd.leagues || (upd.league ? [upd.league] : []);
        if (!curLeagues.some(l => l.toLowerCase() === item.league.toLowerCase())) {
          upd.leagues = [...curLeagues, item.league]; upd.league = upd.leagues[0];
        }
      }
    }
    setForm(upd);
  };

  const handleRemovePlayer = (name) => {
    const updated = (form.players || []).filter(p => p !== name);
    setForm(p => ({ ...p, players: updated, player: updated[0] || '' }));
  };

  const handleToggleMarket = (item) => {
    const name = item.name || item;
    const current = form.markets || [];
    const exists = current.includes(name);
    const updated = exists ? current.filter(m => m !== name) : [...current, name];
    setForm(p => ({ ...p, markets: updated, market: updated[0] || '' }));
  };

  const handleRemoveMarket = (name) => {
    const updated = (form.markets || []).filter(m => m !== name);
    setForm(p => ({ ...p, markets: updated, market: updated[0] || '' }));
  };

  const hasContent = (form.sports?.length > 0) || form.sport || (form.leagues?.length > 0) || form.league || (form.teams?.length > 0) || form.team || (form.players?.length > 0) || form.player || form.market || (form.markets?.length > 0);

  return (
    <View style={st.corrForm} onStartShouldSetResponder={() => { if (openField) { setOpenField(null); return true; } return false; }}>
      {isAdd && (
        <View style={st.field}>
          <Text style={st.fLabel}>Input Term</Text>
          <TextInput style={st.fInput} value={form.input} onChangeText={v => setForm(p => ({ ...p, input: v }))}
            placeholder='e.g. "ice trae 14+ points"' placeholderTextColor="rgba(255,255,255,0.25)" />
        </View>
      )}

      <View style={st.field}>
        <Text style={st.fLabel}>Sport(s){activeSports.length > 1 ? ` (${activeSports.length})` : ''}</Text>
        <View style={st.chipRow}>
          {sportsFromData.map(s => {
            const isOn = activeSports.some(sp => sp.toLowerCase() === s.toLowerCase());
            return (
              <TouchableOpacity key={s} style={[st.chipSm, isOn && st.chipOn]} onPress={() => {
                const newSports = isOn ? activeSports.filter(sp => sp.toLowerCase() !== s.toLowerCase()) : [...activeSports, s];
                setForm(p => ({ ...p, sport: newSports[0] || '', sports: newSports, league: '', leagues: [], team: '', teams: [], away_team: '', player: '', players: [] }));
              }}>
                <Text style={[st.chipSmTxt, isOn && st.chipTxtOn]}>{s}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View style={st.field}>
        <Text style={st.fLabel}>League(s){activeLeagues.length > 1 ? ` (${activeLeagues.length})` : ''}</Text>
        <View style={st.chipRow}>
          {leaguesForSport.map(l => {
            const isOn = activeLeagues.some(lg => lg.toLowerCase() === l.toLowerCase());
            return (
              <TouchableOpacity key={l} style={[st.chipSm, isOn && st.chipOn]} onPress={() => {
                const newLeagues = isOn ? activeLeagues.filter(lg => lg.toLowerCase() !== l.toLowerCase()) : [...activeLeagues, l];
                setForm(p => ({ ...p, league: newLeagues[0] || '', leagues: newLeagues, team: '', teams: [], away_team: '', player: '', players: [] }));
              }}>
                <Text style={[st.chipSmTxt, isOn && st.chipTxtOn]}>{l}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <MultiTagDropdown label="Team(s)" selected={form.teams || (form.team ? [form.team] : [])} placeholder="Search teams..."
        isOpen={openField === 'team'} query={searchQ}
        onOpen={() => { setOpenField('team'); setSearchQ(''); }}
        onClose={() => setOpenField(null)}
        onSearch={setSearchQ}
        items={buildResults(searchQ, teamsForLeague)}
        onToggle={handleToggleTeam}
        onRemove={handleRemoveTeam}
        tagColor="#60a5fa"
        renderItem={(item, sel) => <><View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}><Text style={st.ddName}>{item.name}</Text><Text style={st.ddMeta}>{item.league?.toUpperCase()}</Text></View>{sel && <Ionicons name="checkmark-circle" size={16} color="#10b981" />}</>}
        getKey={item => item.name} />

      <SearchableDropdown label="Opponent / Away Team" value={form.away_team} placeholder="Search opponent..."
        isOpen={openField === 'away_team'} query={searchQ}
        onOpen={() => { setOpenField('away_team'); setSearchQ(form.away_team || ''); }}
        onClose={() => setOpenField(null)}
        onSearch={setSearchQ}
        items={buildResults(searchQ, teamsForLeague)}
        onSelect={handleSelectAwayTeam}
        renderItem={(item) => <><Text style={st.ddName}>{item.name}</Text><Text style={st.ddMeta}>{item.league?.toUpperCase()}</Text></>}
        onClear={() => setForm(p => ({ ...p, away_team: '' }))} />

      <MultiTagDropdown label={`Player(s)${playersForTeam.length ? ` (${playersForTeam.length} available)` : ''}`} selected={form.players || (form.player ? [form.player] : [])} placeholder={playersForTeam.length ? `Search ${playersForTeam.length} players...` : 'Type a player name...'}
        isOpen={openField === 'player'} query={searchQ}
        onOpen={() => { setOpenField('player'); setSearchQ(''); }}
        onClose={() => setOpenField(null)}
        onSearch={setSearchQ}
        items={buildResults(searchQ, playersForTeam)}
        onToggle={handleTogglePlayer}
        onRemove={handleRemovePlayer}
        tagColor="#c084fc"
        renderItem={(item, sel) => <><View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}><Text style={st.ddName}>{item.name}</Text><Text style={st.ddMeta}>{item.team} · {item.league?.toUpperCase()}</Text></View>{sel && <Ionicons name="checkmark-circle" size={16} color="#10b981" />}</>}
        getKey={item => item.name} />

      <MultiTagDropdown label={`Market(s)${activeSports.length ? ` — ${marketsForSport.length} for ${activeSports.join('/')}` : ''}`} selected={form.markets || []} placeholder={activeSports.length ? `Search ${marketsForSport.length} markets...` : 'Select a sport first, or search all markets...'}
        isOpen={openField === 'market'} query={searchQ}
        onOpen={() => { setOpenField('market'); setSearchQ(''); }}
        onClose={() => setOpenField(null)}
        onSearch={setSearchQ}
        items={buildResults(searchQ, marketsForSport)}
        onToggle={handleToggleMarket}
        onRemove={handleRemoveMarket}
        tagColor="#f59e0b"
        renderItem={(item, sel) => <><View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}><Text style={st.ddName}>{item.name || item}</Text>{item.sports?.length > 0 && <Text style={st.ddMeta}>{item.sports.slice(0,2).join(', ')}</Text>}</View>{sel && <Ionicons name="checkmark-circle" size={16} color="#10b981" />}</>}
        getKey={item => item.name || item} />

      <View style={{ flexDirection: 'row', gap: 10 }}>
        <View style={[st.field, { flex: 1 }]}>
          <Text style={st.fLabel}>Line / Threshold</Text>
          <TextInput style={st.fInput} value={form.line} onChangeText={v => setForm(p => ({ ...p, line: v }))}
            placeholder="e.g. 14, -3.5, 225.5" placeholderTextColor="rgba(255,255,255,0.25)" keyboardType="numeric" />
        </View>
        <View style={[st.field, { flex: 1 }]}>
          <Text style={st.fLabel}>Direction</Text>
          <View style={st.chipRow}>
            {directionOptions.map(d => (
              <TouchableOpacity key={d} style={[st.chip, form.direction === d && st.chipOn, { flex: 1, alignItems: 'center' }]}
                onPress={() => setForm(p => ({ ...p, direction: p.direction === d ? '' : d }))}>
                <Text style={[st.chipTxt, form.direction === d && st.chipTxtOn]}>{d.charAt(0).toUpperCase() + d.slice(1)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Sportsbooks — none selected means applies to all */}
      <MultiTagDropdown
        label={`Sportsbook(s)${(form.sportsbooks?.length > 0) ? ` (${form.sportsbooks.length})` : ' — none = all'}`}
        selected={form.sportsbooks || []}
        placeholder="Search sportsbooks... (leave empty = all)"
        isOpen={openField === 'sportsbook'}
        query={searchQ}
        onOpen={() => { setOpenField('sportsbook'); setSearchQ(''); }}
        onClose={() => setOpenField(null)}
        onSearch={setSearchQ}
        items={buildResults(searchQ, ALL_SPORTSBOOK_NAMES)}
        onToggle={(item) => {
          const name = typeof item === 'string' ? item : item.name;
          const current = form.sportsbooks || [];
          const updated = current.includes(name) ? current.filter(s => s !== name) : [...current, name];
          setForm(p => ({ ...p, sportsbooks: updated }));
        }}
        onRemove={(name) => setForm(p => ({ ...p, sportsbooks: (p.sportsbooks || []).filter(s => s !== name) }))}
        tagColor="#f472b6"
        renderItem={(item, sel) => (
          <><Text style={st.ddName}>{typeof item === 'string' ? item : item.name}</Text>{sel && <Ionicons name="checkmark-circle" size={16} color="#10b981" />}</>
        )}
        getKey={item => typeof item === 'string' ? item : item.name}
      />

      {/* Query Type — SGP / LIVE / STRAIGHT */}
      <View style={st.field}>
        <Text style={st.fLabel}>Query Type(s){(form.queryTypes?.length > 0) ? '' : ' — none = all'}</Text>
        <View style={st.chipRow}>
          {QUERY_TYPES.map(qt => {
            const isOn = (form.queryTypes || []).includes(qt);
            const color = qt === 'SGP' ? '#8b5cf6' : qt === 'LIVE' ? '#10b981' : '#60a5fa';
            return (
              <TouchableOpacity
                key={qt}
                style={[st.chip, isOn && st.chipOn, isOn && { borderColor: color, backgroundColor: color + '22' }]}
                onPress={() => {
                  const current = form.queryTypes || [];
                  const updated = current.includes(qt) ? current.filter(q => q !== qt) : [...current, qt];
                  setForm(p => ({ ...p, queryTypes: updated }));
                }}
              >
                <Text style={[st.chipTxt, isOn && { color }]}>{qt}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <MappingSummary form={form} isAdd={isAdd} />

      <View style={st.formActions}>
        <TouchableOpacity style={[st.saveBtn, !hasContent && { opacity: 0.35 }]} onPress={onSubmit}
          disabled={!hasContent || loading}>
          {loading ? <ActivityIndicator size="small" color="#fff" /> :
            <><Ionicons name="checkmark" size={14} color="#fff" /><Text style={st.saveTxt}>{isAdd ? 'Add Resolution' : 'Save Correction'}</Text></>}
        </TouchableOpacity>
        {onCancel && <TouchableOpacity style={st.cancelBtn} onPress={onCancel}><Text style={st.cancelTxt}>Cancel</Text></TouchableOpacity>}
      </View>
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Mapping Summary — shows the full structured breakdown
// ═══════════════════════════════════════════════════════════════════════
export function MappingSummary({ form, isAdd }) {
  const sports = form.sports?.length > 0 ? form.sports : (form.sport ? [form.sport] : []);
  const leagues = form.leagues?.length > 0 ? form.leagues : (form.league ? [form.league] : []);
  const teams = form.teams?.length > 0 ? form.teams : (form.team ? [form.team] : []);
  const players = form.players?.length > 0 ? form.players : (form.player ? [form.player] : []);
  const markets = form.markets?.length > 0 ? form.markets : (form.market ? [form.market] : []);
  const sportsbooks = form.sportsbooks?.length > 0 ? form.sportsbooks : [];
  const queryTypes = form.queryTypes?.length > 0 ? form.queryTypes : [];
  const parts = [
    ...sports.map(s => ['Sport', s, '#8b5cf6']),
    ...leagues.map(l => ['League', l?.toUpperCase(), '#3b82f6']),
    ...teams.map(t => ['Team', t, '#60a5fa']),
    form.away_team && ['vs', form.away_team, '#60a5fa'],
    ...players.map(p => ['Player', p, '#c084fc']),
    ...markets.map(m => ['Market', m, '#f59e0b']),
    form.line && ['Line', form.line, '#fff'],
    form.direction && ['Direction', form.direction?.charAt(0).toUpperCase() + form.direction?.slice(1), form.direction === 'over' ? '#10b981' : '#ef4444'],
    sportsbooks.length > 0 && ['Books', sportsbooks.length === 1 ? sportsbooks[0] : `${sportsbooks.length} books`, '#f472b6'],
    queryTypes.length > 0 && ['Type', queryTypes.join(' / '), '#a78bfa'],
  ].filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <View style={st.summaryBox}>
      <Text style={st.summaryTitle}>Resolution Preview</Text>
      {isAdd && form.input ? <Text style={st.summaryInput}>"{form.input}"  =</Text> : null}
      <View style={st.summaryFields}>
        {parts.map(([label, value, color], i) => (
          <View key={`${label}-${value}-${i}`} style={st.summaryTag}>
            <Text style={st.summaryKey}>{label}</Text>
            <Text style={[st.summaryVal, { color }]}>{value}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Searchable Dropdown
// ═══════════════════════════════════════════════════════════════════════
export function SearchableDropdown({ label, value, placeholder, isOpen, query, onOpen, onClose, onSearch, items, onSelect, renderItem, onClear }) {
  const inputRef = React.useRef(null);
  return (
    <View style={[st.field, isOpen && { zIndex: 50 }]}>
      <Text style={st.fLabel}>{label}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TextInput
          ref={inputRef}
          style={[st.fInput, { flex: 1 }, isOpen && { borderColor: 'rgba(59,130,246,0.4)' }]}
          value={isOpen ? query : value}
          placeholder={placeholder}
          placeholderTextColor="rgba(255,255,255,0.25)"
          onFocus={() => onOpen()}
          onChangeText={v => { if (!isOpen) onOpen(); onSearch(v); }}
        />
        {value ? (
          <TouchableOpacity style={st.clearBtn} onPress={() => { onClear(); onClose(); }}>
            <Ionicons name="close-circle" size={16} color="rgba(255,255,255,0.3)" />
          </TouchableOpacity>
        ) : null}
      </View>
      {isOpen && (
        <>
          {Platform.OS === 'web' && (
            <TouchableOpacity style={st.ddBackdrop} activeOpacity={1} onPress={() => { inputRef.current?.blur(); onClose(); }} />
          )}
          {items?.length > 0 && (
            <View style={st.dropdown}>
              <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                {items.map((item, i) => (
                  <TouchableOpacity key={`${item.name || item}:${i}`} style={st.ddItem} onPress={() => onSelect(item)}>
                    {renderItem(item)}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </>
      )}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Multi-Tag Dropdown — stays open, shows checkmarks, tags above
// ═══════════════════════════════════════════════════════════════════════
export function MultiTagDropdown({ label, selected = [], placeholder, isOpen, query, onOpen, onClose, onSearch, items, onToggle, onRemove, renderItem, getKey, tagColor = '#f59e0b' }) {
  const inputRef = React.useRef(null);
  const selSet = useMemo(() => new Set(selected.map(s => (typeof s === 'string' ? s : s.name || s).toLowerCase())), [selected]);

  return (
    <View style={[st.field, isOpen && { zIndex: 50 }]}>
      {!!label && <Text style={st.fLabel}>{label}</Text>}
      {selected.length > 0 && (
        <View style={st.marketTags}>
          {selected.map(val => (
            <View key={val} style={[st.marketTag, { borderColor: tagColor + '40' }]}>
              <Text style={[st.marketTagTxt, { color: tagColor }]}>{val}</Text>
              <TouchableOpacity onPress={() => onRemove(val)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                <Ionicons name="close-circle" size={14} color={tagColor + '99'} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TextInput
          ref={inputRef}
          style={[st.fInput, { flex: 1 }, isOpen && { borderColor: 'rgba(59,130,246,0.4)' }]}
          value={isOpen ? query : ''}
          placeholder={selected.length > 0 ? `${selected.length} selected — search to add more...` : placeholder}
          placeholderTextColor="rgba(255,255,255,0.25)"
          onFocus={() => onOpen()}
          onChangeText={v => { if (!isOpen) onOpen(); onSearch(v); }}
        />
      </View>
      {isOpen && (
        <>
          {Platform.OS === 'web' && (
            <TouchableOpacity style={st.ddBackdrop} activeOpacity={1} onPress={() => { inputRef.current?.blur(); onClose(); }} />
          )}
          {items?.length > 0 && (
            <View style={st.dropdown}>
              <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                {items.map((item, i) => {
                  const key = getKey(item);
                  const isSel = selSet.has(key.toLowerCase());
                  return (
                    <TouchableOpacity key={`${key}:${i}`} style={[st.ddItem, isSel && { backgroundColor: 'rgba(16,185,129,0.08)' }]} onPress={() => onToggle(item)}>
                      {renderItem(item, isSel)}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </>
      )}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Bulk + Select All
// ═══════════════════════════════════════════════════════════════════════
export function BulkBar({ count, onApprove, onReject, loading }) {
  return (
    <View style={st.bulkBar}>
      <Text style={st.bulkTxt}>{count} selected</Text>
      <TouchableOpacity style={st.bulkGreen} onPress={onApprove} disabled={loading}>
        {loading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="checkmark-circle" size={14} color="#fff" /><Text style={st.bulkBtnTxt}>Approve</Text></>}
      </TouchableOpacity>
      <TouchableOpacity style={st.bulkRed} onPress={onReject} disabled={loading}>
        <Ionicons name="close-circle" size={14} color="#fff" /><Text style={st.bulkBtnTxt}>Reject</Text>
      </TouchableOpacity>
    </View>
  );
}

export function SelectAllRow({ all, onToggle, count }) {
  return (
    <TouchableOpacity style={st.selAll} onPress={onToggle}>
      <View style={[st.cb, all && st.cbOn]}>{all && <Ionicons name="checkmark" size={12} color="#000" />}</View>
      <Text style={st.selAllTxt}>{all ? 'Deselect all' : `Select all ${count}`}</Text>
    </TouchableOpacity>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Query Type Legend + Confidence Guide
// ═══════════════════════════════════════════════════════════════════════
export function QueryTypeLegend() {
  const [open, setOpen] = useState(false);
  return (
    <View style={st.legendWrap}>
      <TouchableOpacity style={st.legendToggle} onPress={() => setOpen(p => !p)}>
        <Ionicons name="information-circle-outline" size={14} color="rgba(255,255,255,0.4)" />
        <Text style={st.legendToggleTxt}>Query Types & Confidence Guide</Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={14} color="rgba(255,255,255,0.3)" />
      </TouchableOpacity>
      {open && (
        <View style={st.legendBody}>
          <View style={st.legendSection}>
            <Text style={st.legendSectionTitle}>Query Types</Text>
            {Object.entries(QUERY_TYPE_INFO).map(([key, info]) => (
              <View key={key} style={st.legendRow}>
                <View style={[st.legendDot, { backgroundColor: info.color + '30' }]}>
                  <Ionicons name={info.icon} size={11} color={info.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[st.legendLabel, { color: info.color }]}>{info.label}</Text>
                  <Text style={st.legendDesc}>{info.desc}</Text>
                </View>
              </View>
            ))}
          </View>
          <View style={[st.legendSection, { marginTop: 10 }]}>
            <Text style={st.legendSectionTitle}>Confidence Levels</Text>
            {CONFIDENCE_LEVELS.map(l => (
              <View key={l.label} style={st.legendRow}>
                <View style={[st.legendDot, { backgroundColor: l.color + '30' }]}>
                  <Ionicons name={l.icon} size={11} color={l.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[st.legendLabel, { color: l.color }]}>{l.label} ({l.range})</Text>
                  <Text style={st.legendDesc}>{l.desc}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Query Tester — live sandbox for testing queries and training the system
// ═══════════════════════════════════════════════════════════════════════
export function QueryTester({ onSendToReview }) {
  const base = getBase();
  const [query, setQuery] = useState('');
  const [batchText, setBatchText] = useState('');
  const [forceRecord, setForceRecord] = useState(false);
  const [running, setRunning] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  const runSingle = async (q) => {
    const t0 = Date.now();
    const res = await fetch(`${base}/nlOdds`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: q, defaults: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, now: new Date().toISOString() } })
    });
    const data = await res.json();
    const elapsed = Date.now() - t0;
    const qr = data._queryResolution || {};
    const ent = qr.entities || {};
    const results = data.results || [];
    return {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      query: q, elapsed,
      resultCount: results.length,
      queryType: qr.type || data._classificationType || '?',
      confidence: qr.confidence || 0,
      fastPathHit: !!data._fastPathHit,
      entities: ent,
      sampleResults: results.slice(0, 3).map(r => ({
        name: r.name || r.market || '—',
        team: r.team || r.home_team || '—',
        book: r.book || r.sportsbook || '—',
        price: r.price ?? r.odds ?? '—',
        ev: r.ev_percent != null ? `${r.ev_percent > 0 ? '+' : ''}${r.ev_percent.toFixed(1)}%` : '—'
      })),
      saved: false,
      rawQueryResolution: qr
    };
  };

  const handleRun = async () => {
    if (!query.trim()) return;
    setRunning(true);
    try {
      const result = await runSingle(query.trim());
      setHistory(prev => [result, ...prev]);
      setExpanded(prev => new Set([...prev, result.id]));
    } catch (e) {
      setHistory(prev => [{ id: Date.now().toString(), query: query.trim(), error: e.message, elapsed: 0, resultCount: 0 }, ...prev]);
    }
    setRunning(false);
  };

  const handleBatchRun = async () => {
    const queries = batchText.split('\n').map(q => q.trim()).filter(q => q.length > 0);
    if (queries.length === 0) return;
    setRunning(true);
    const results = [];
    for (const q of queries) {
      try {
        const result = await runSingle(q);
        results.push(result);
      } catch (e) {
        results.push({ id: Date.now().toString(), query: q, error: e.message, elapsed: 0, resultCount: 0 });
      }
    }
    setHistory(prev => [...results, ...prev]);
    setRunning(false);
  };

  const handleSendToReview = async (item) => {
    try {
      const resp = await erFetch(`${base}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'saveTestResult', query: item.query,
          entities: item.entities, queryType: item.queryType,
          resultCount: item.resultCount, confidence: item.confidence,
          forceRecord, sendToReview: true
        })
      });
      const json = await resp.json();
      if (!json.success || !json.resolutionId) return;
      // Force status to 'pending' so it appears in the review queue
      // (handles docs that already existed with confirmed/corrected/rejected status)
      await erFetch(`${base}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'review', docId: json.resolutionId, status: 'pending' })
      });
      setHistory(prev => prev.map(h => h.id === item.id ? { ...h, sentToReview: true } : h));
      if (onSendToReview) setTimeout(() => onSendToReview(item.query), 600);
    } catch (_) {}
  };

  const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const DimTag = ({ label, value, color }) => (
    <View style={tst.dimTag}>
      <Text style={tst.dimKey}>{label}</Text>
      <Text style={[tst.dimVal, { color: value ? color : 'rgba(255,255,255,0.12)' }]}>{value || '—'}</Text>
    </View>
  );

  return (
    <View>
      <Text style={st.title}>Query Tester</Text>
      <Text style={tst.desc}>Test real queries against the live pipeline. See what the system resolves, then send to the review queue for correction.</Text>

      {/* Toggle bar */}
      <View style={tst.toggleRow}>
        <TouchableOpacity style={[tst.modeBtn, !batchMode && tst.modeBtnOn]} onPress={() => setBatchMode(false)}>
          <Ionicons name="search" size={13} color={!batchMode ? '#fff' : 'rgba(255,255,255,0.4)'} />
          <Text style={[tst.modeTxt, !batchMode && { color: '#fff' }]}>Single</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[tst.modeBtn, batchMode && tst.modeBtnOn]} onPress={() => setBatchMode(true)}>
          <Ionicons name="list" size={13} color={batchMode ? '#fff' : 'rgba(255,255,255,0.4)'} />
          <Text style={[tst.modeTxt, batchMode && { color: '#fff' }]}>Batch</Text>
        </TouchableOpacity>
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={tst.forceToggle} onPress={() => setForceRecord(p => !p)}>
          <View style={[tst.toggleDot, forceRecord && tst.toggleDotOn]} />
          <Text style={[tst.forceTxt, forceRecord && { color: '#f59e0b' }]}>Force re-learn</Text>
        </TouchableOpacity>
      </View>

      {/* Input area */}
      {!batchMode ? (
        <View style={tst.inputRow}>
          <TextInput style={[st.fInput, { flex: 1 }]} value={query} onChangeText={setQuery}
            placeholder='e.g. "cubs run line", "kat rebounds over 8.5"' placeholderTextColor="rgba(255,255,255,0.25)"
            onSubmitEditing={handleRun} returnKeyType="search" />
          <TouchableOpacity style={[tst.runBtn, running && { opacity: 0.5 }]} onPress={handleRun} disabled={running}>
            {running ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="play" size={14} color="#fff" /><Text style={tst.runTxt}>Run</Text></>}
          </TouchableOpacity>
        </View>
      ) : (
        <View>
          <TextInput style={tst.batchInput} value={batchText} onChangeText={setBatchText}
            placeholder={'Paste queries, one per line:\ncubs run line\nkat rebounds over 8.5\nnba moneyline tonight'}
            placeholderTextColor="rgba(255,255,255,0.2)" multiline numberOfLines={6} textAlignVertical="top" />
          <TouchableOpacity style={[tst.runBtn, { alignSelf: 'flex-start', marginTop: 6 }, running && { opacity: 0.5 }]} onPress={handleBatchRun} disabled={running}>
            {running ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="play" size={14} color="#fff" /><Text style={tst.runTxt}>Run {batchText.split('\n').filter(l => l.trim()).length} queries</Text></>}
          </TouchableOpacity>
        </View>
      )}

      {forceRecord && (
        <View style={tst.forceNotice}>
          <Ionicons name="alert-circle" size={13} color="#f59e0b" />
          <Text style={tst.forceNoticeTxt}>Force re-learn is ON — saved results will overwrite existing fast paths</Text>
        </View>
      )}

      {/* Results history */}
      {history.length > 0 && (
        <View style={{ marginTop: 14 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={st.boxLabel}>RESULTS ({history.length})</Text>
            <TouchableOpacity onPress={() => setHistory([])}><Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>Clear</Text></TouchableOpacity>
          </View>
          {history.map(item => (
            <View key={item.id} style={tst.resultCard}>
              <TouchableOpacity style={tst.resultHeader} onPress={() => toggleExpand(item.id)}>
                <View style={{ flex: 1 }}>
                  <Text style={tst.resultQuery}>"{item.query}"</Text>
                  <View style={tst.resultMeta}>
                    {item.error ? (
                      <Text style={{ color: '#ef4444', fontSize: 10 }}>Error: {item.error}</Text>
                    ) : (
                      <>
                        <View style={[tst.metaBadge, { backgroundColor: item.resultCount > 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)' }]}>
                          <Text style={[tst.metaBadgeTxt, { color: item.resultCount > 0 ? '#10b981' : '#ef4444' }]}>{item.resultCount} results</Text>
                        </View>
                        <Text style={tst.metaTime}>{(item.elapsed / 1000).toFixed(1)}s</Text>
                        {item.fastPathHit && (
                          <View style={[tst.metaBadge, { backgroundColor: 'rgba(139,92,246,0.12)' }]}>
                            <Ionicons name="flash" size={9} color="#8b5cf6" />
                            <Text style={[tst.metaBadgeTxt, { color: '#8b5cf6' }]}>Fast Path</Text>
                          </View>
                        )}
                        <Text style={tst.metaTime}>{(item.confidence * 100).toFixed(0)}% conf</Text>
                        {item.sentToReview && (
                          <View style={[tst.metaBadge, { backgroundColor: 'rgba(167,139,250,0.12)' }]}>
                            <Ionicons name="checkmark-circle" size={9} color="#a78bfa" />
                            <Text style={[tst.metaBadgeTxt, { color: '#a78bfa' }]}>In Review</Text>
                          </View>
                        )}
                      </>
                    )}
                  </View>
                </View>
                <Ionicons name={expanded.has(item.id) ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.3)" />
              </TouchableOpacity>

              {expanded.has(item.id) && !item.error && (
                <View style={tst.resultBody}>
                  {/* Resolution mapping */}
                  <Text style={st.boxLabel}>RESOLVED MAPPING</Text>
                  <View style={tst.dimGrid}>
                    <DimTag label="Sport" value={item.entities?.sport} color="#8b5cf6" />
                    <DimTag label="League" value={item.entities?.league?.toUpperCase()} color="#3b82f6" />
                    <DimTag label="Team" value={item.entities?.team} color="#60a5fa" />
                    <DimTag label="Player" value={item.entities?.player} color="#c084fc" />
                    <DimTag label="Market" value={item.entities?.market} color="#f59e0b" />
                    <DimTag label="Line" value={item.entities?.line} color="#fff" />
                    <DimTag label="Direction" value={item.entities?.direction} color={item.entities?.direction === 'over' ? '#10b981' : '#ef4444'} />
                    <DimTag label="Query Type" value={item.queryType} color="#6b7280" />
                  </View>

                  {/* Sample results */}
                  {item.sampleResults?.length > 0 && (
                    <View style={{ marginTop: 8 }}>
                      <Text style={st.boxLabel}>SAMPLE RESULTS (top 3)</Text>
                      {item.sampleResults.map((sr, i) => (
                        <View key={i} style={tst.sampleRow}>
                          <Text style={tst.sampleName} numberOfLines={1}>{sr.name}</Text>
                          <Text style={tst.sampleTeam} numberOfLines={1}>{sr.team}</Text>
                          <Text style={tst.sampleBook}>{sr.book}</Text>
                          <Text style={tst.samplePrice}>{sr.price}</Text>
                          <Text style={[tst.sampleEv, sr.ev?.startsWith('+') && { color: '#10b981' }]}>{sr.ev}</Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Send to review queue */}
                  {!item.sentToReview ? (
                    <TouchableOpacity style={tst.reviewBtn} onPress={() => handleSendToReview(item)}>
                      <Ionicons name="arrow-forward-circle" size={14} color="#a78bfa" />
                      <Text style={tst.reviewTxt}>Send to Review Queue</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={tst.sentRow}>
                      <View style={tst.sentBadge}>
                        <Ionicons name="checkmark-circle" size={13} color="#a78bfa" />
                        <Text style={tst.sentTxt}>Sent to Review Queue</Text>
                      </View>
                      {onSendToReview && (
                        <TouchableOpacity style={tst.viewBtn} onPress={() => onSendToReview(item.query)}>
                          <Text style={tst.viewTxt}>View in Queue</Text>
                          <Ionicons name="arrow-forward" size={12} color="#a78bfa" />
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              )}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const tst = {
  desc: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 12, lineHeight: 17 },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 },
  modeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 5, paddingHorizontal: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.04)' },
  modeBtnOn: { backgroundColor: 'rgba(255,255,255,0.12)' },
  modeTxt: { color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: '600' },
  forceToggle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  toggleDot: { width: 32, height: 18, borderRadius: 9, backgroundColor: 'rgba(255,255,255,0.08)', justifyContent: 'center', paddingHorizontal: 2 },
  toggleDotOn: { backgroundColor: 'rgba(245,158,11,0.3)' },
  forceTxt: { color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: '600' },
  forceNotice: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 5, paddingHorizontal: 8, borderRadius: 6, backgroundColor: 'rgba(245,158,11,0.06)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.15)', marginTop: 6 },
  forceNoticeTxt: { color: '#f59e0b', fontSize: 10, fontWeight: '500' },
  inputRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  runBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, backgroundColor: '#3b82f6' },
  runTxt: { color: '#fff', fontWeight: '700', fontSize: 12 },
  batchInput: { minHeight: 100, padding: 10, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace', backgroundColor: 'rgba(255,255,255,0.02)' },
  resultCard: { borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)', marginBottom: 6, overflow: 'hidden' },
  resultHeader: { flexDirection: 'row', alignItems: 'center', padding: 10, gap: 8 },
  resultQuery: { color: '#f59e0b', fontSize: 12, fontWeight: '600' },
  resultMeta: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' },
  metaBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 1, paddingHorizontal: 6, borderRadius: 6 },
  metaBadgeTxt: { fontSize: 10, fontWeight: '600' },
  metaTime: { color: 'rgba(255,255,255,0.3)', fontSize: 10 },
  resultBody: { padding: 10, paddingTop: 0, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.04)' },
  dimGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4, marginBottom: 4 },
  dimTag: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 2, paddingHorizontal: 7, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' },
  dimKey: { color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: '700' },
  dimVal: { fontSize: 11, fontWeight: '600' },
  sampleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 3, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  sampleName: { color: 'rgba(255,255,255,0.7)', fontSize: 10, flex: 2 },
  sampleTeam: { color: 'rgba(255,255,255,0.4)', fontSize: 10, flex: 1.5 },
  sampleBook: { color: 'rgba(96,165,250,0.7)', fontSize: 9, width: 60 },
  samplePrice: { color: 'rgba(255,255,255,0.6)', fontSize: 10, width: 40, textAlign: 'right' },
  sampleEv: { color: 'rgba(255,255,255,0.4)', fontSize: 10, width: 45, textAlign: 'right' },
  reviewBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 8, paddingVertical: 7, paddingHorizontal: 14, borderRadius: 7, backgroundColor: 'rgba(167,139,250,0.12)', borderWidth: 1, borderColor: 'rgba(167,139,250,0.25)', alignSelf: 'flex-start' },
  reviewTxt: { color: '#a78bfa', fontWeight: '700', fontSize: 11 },
  sentRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  sentBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, backgroundColor: 'rgba(167,139,250,0.06)' },
  sentTxt: { color: 'rgba(167,139,250,0.6)', fontWeight: '600', fontSize: 11 },
  viewBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(167,139,250,0.2)' },
  viewTxt: { color: '#a78bfa', fontWeight: '700', fontSize: 11 },
};

// ═══════════════════════════════════════════════════════════════════════
// Metrics View
// ═══════════════════════════════════════════════════════════════════════
export function MetricsView({ stats }) {
  return (
    <View>
      <Text style={st.title}>Resolution Metrics</Text>
      <View style={st.metricsRow}>
        {[['By Type', stats.by_type], ['By Source', stats.by_source], ['By Status', stats.by_status]].map(([t, d]) => (
          <View key={t} style={st.metricCard}>
            <Text style={st.metricTitle}>{t}</Text>
            {Object.entries(d || {}).map(([k, v]) => (
              <View key={k} style={st.mRow}>
                {t === 'By Status' && <View style={[st.dot, { backgroundColor: STATUS_COLORS[k] || '#888' }]} />}
                <Text style={st.mKey}>{k}</Text><Text style={st.mVal}>{v}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
      {stats.top_used?.length > 0 && (
        <View style={[st.card, { marginTop: 14 }]}>
          <Text style={st.metricTitle}>Top 20 Most Used</Text>
          {stats.top_used.map((r, i) => (
            <View key={r.id} style={st.topRow}>
              <Text style={st.topRank}>{i + 1}.</Text>
              <Text style={st.topIn}>"{r.input}"</Text>
              <Ionicons name="arrow-forward" size={11} color="rgba(255,255,255,0.2)" />
              <Text style={st.topOut}>{r.resolved}</Text>
              <Text style={st.topCt}>{r.use_count}x</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════
